import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    reduce, set, flow, unset, get, identity, map
} from 'lodash/fp';
import { bindActionCreators } from 'redux';
import FilterHeaderInternal from './FilterHeaderInternal';
import * as filterHeaderPropTypes from './propTypes';
import actionFactory, { internalActions } from './actions';
import * as selectors from './selectors';
import { buildReducer } from './reducer';

const typeMap = {
    [filterHeaderPropTypes.filterTypes.string]: 'String',
    [filterHeaderPropTypes.filterTypes.number]: 'Number',
    [filterHeaderPropTypes.filterTypes.date]: 'Date',
    [filterHeaderPropTypes.filterTypes.dateFromOnly]: 'Date',
    [filterHeaderPropTypes.filterTypes.dateToOnly]: 'Date',
    [filterHeaderPropTypes.filterTypes.list]: 'List',
    [filterHeaderPropTypes.filterTypes.radioList]: 'List'
};

const mapType = (type) => (
    typeMap[type] || 'String'
);

const extractFilters = (filters) => reduce((result, filter) => {
    if (filter.value !== undefined) {
        const adaptedFilter = flow(
            unset('definition'),
            set('title', filter.definition.title),
            set('type', mapType(filter.definition.type)),
            filter.definition.type === filterHeaderPropTypes.filterTypes.dateFromOnly
                ? set('fromOnly', true)
                : identity,
            filter.definition.type === filterHeaderPropTypes.filterTypes.dateToOnly
                ? set('toOnly', true)
                : identity
        )(filter);
        result.push(adaptedFilter);
    }
    return result;
}, [], filters);

const extractFilterDefinitions = (filters) => reduce((result, filter) => {
    result.push(flow(
        get('definition'),
        filter.definition.type === filterHeaderPropTypes.filterTypes.radioList ? set('radio', true) : identity,
        filter.definition.availableValues
            ? set(
                'availableValues',
                map((av) => ({
                    ...av,
                    name: av.value,
                    value: av.title
                }))(filter.definition.availableValues)
            )
            : identity
    )(filter));
    return result;
}, [], filters);

class FilterHeader extends React.Component {
    constructor(props) {
        super(props);
        this.getAvailableListValues = this.getAvailableListValues.bind(this);
        this.openFilter = this.openFilter.bind(this);
        this.openDefaultFilter = this.openDefaultFilter.bind(this);
        this.addFilter = this.addFilter.bind(this);
        this.removeFilter = this.removeFilter.bind(this);
        this.updateFilter = this.updateFilter.bind(this);
        this.addDefaultFilter = this.addDefaultFilter.bind(this);
        this.removeDefaultFilter = this.removeDefaultFilter.bind(this);
        this.updateDefaultFilter = this.updateDefaultFilter.bind(this);
        this.actionCreators = bindActionCreators(actionFactory(this.props.id), this.props.dispatch);
        buildReducer(this.props.id);
    }

    componentDidMount() {
        this.props.loaded(this.props.id);
    }

    getAvailableListValues(filterTitle) {
        const filter = this.props.filters.find((f) => f.definition.title === filterTitle);
        if (filter) {
            this.actionCreators.fetchAvailableListValues(
                filter.definition.id,
                filter.definition.availableValuesLocation
            );
        }
    }

    openFilter(filterTitle) {
        if (filterTitle === 'ADD_FILTER') {
            this.actionCreators.openFilter('ADD_FILTER');
        } else if (filterTitle === 'SET_DEFAULT_FILTERS') {
            this.actionCreators.openFilter('SET_DEFAULT_FILTERS');
        } else if (!filterTitle) {
            this.actionCreators.openFilter(null);
        } else {
            const filter = this.props.filters.find((f) => f.definition.title === filterTitle);
            if (filter) { this.actionCreators.openFilter(filter.definition.id); }
        }
    }

    openDefaultFilter(filterTitle) {
        if (filterTitle === 'ADD_FILTER') {
            this.actionCreators.openDefaultFilter('ADD_FILTER');
        } else if (!filterTitle) {
            this.actionCreators.openDefaultFilter(null);
        } else {
            const filter = this.props.defaultFilters.find((f) => f.definition.title === filterTitle);
            if (filter) { this.actionCreators.openDefaultFilter(filter.definition.id); }
        }
    }

    addFilter(filterTitle) {
        const filter = this.props.filters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.addFilter(filter.definition.id); }
    }

    removeFilter(filterTitle) {
        const filter = this.props.filters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.removeFilter(filter.definition.id); }
    }

    updateFilter(filterTitle, value) {
        const filter = this.props.filters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.updateFilter(filter.definition.id, value); }
    }

    addDefaultFilter(filterTitle) {
        const filter = this.props.defaultFilters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.addDefaultFilter(filter.definition.id); }
    }

    removeDefaultFilter(filterTitle) {
        const filter = this.props.defaultFilters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.removeDefaultFilter(filter.definition.id); }
    }

    updateDefaultFilter(filterTitle, value) {
        const filter = this.props.defaultFilters.find((f) => f.definition.title === filterTitle);
        if (filter) { this.actionCreators.updateDefaultFilter(filter.definition.id, value); }
    }

    translateCurrentlyOpenFilter(filterId, filterCollection = this.props.filters) {
        if (filterId === 'ADD_FILTER') { return filterId; }
        if (filterId === 'SET_DEFAULT_FILTERS') { return filterId; }
        const filter = filterCollection.find((f) => f.definition.id === filterId);
        if (filter) {
            return filter.definition.title;
        }

        return null;
    }

    render() {
        return (
            <FilterHeaderInternal
                title={ this.props.title }
                fixed={ this.props.fixed }
                disabled={ this.props.disabled }
                filters={ extractFilters(this.props.filters) }
                filterDefinitions={ extractFilterDefinitions(this.props.filters) }
                filterErrors={ this.props.filterErrors }
                currentlyOpenFilter={
                    this.translateCurrentlyOpenFilter(
                        this.props.currentlyOpenFilter,
                        this.props.filters
                    )
                }
                currentlyOpenDefaultFilter={
                    this.translateCurrentlyOpenFilter(
                        this.props.currentlyOpenDefaultFilter,
                        this.props.defaultFilters
                    )
                }
                openFilter={ this.openFilter }
                openDefaultFilter={ this.openDefaultFilter }
                addFilter={ this.addFilter }
                removeFilter={ this.removeFilter }
                updateFilter={ this.updateFilter }
                addDefaultFilter={ this.addDefaultFilter }
                removeDefaultFilter={ this.removeDefaultFilter }
                updateDefaultFilter={ this.updateDefaultFilter }
                getAvailableListValues={ this.getAvailableListValues }
                defaultFilters={ extractFilters(this.props.defaultFilters) }
                resetFilters={ this.actionCreators.resetFilters }
                styles={ this.props.styles }
                showDefaultFilterMenu={ this.props.showDefaultFilterMenu }
            />
        );
    }
}

FilterHeader.propTypes = {
    title: PropTypes.string,
    fixed: PropTypes.bool,
    disabled: PropTypes.bool,
    filters: filterHeaderPropTypes.filtersPropType,
    defaultFilters: filterHeaderPropTypes.filtersPropType,
    filterErrors: PropTypes.arrayOf(PropTypes.shape({})),
    currentlyOpenFilter: PropTypes.string,
    currentlyOpenDefaultFilter: PropTypes.string,
    refreshControls: PropTypes.shape({
        onClick: PropTypes.func,
        timeLastUpdated: PropTypes.number
    }),
    styles: PropTypes.objectOf(PropTypes.string),
    loaded: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    showDefaultFilterMenu: PropTypes.bool
};

FilterHeader.defaultProps = {
    title: '',
    fixed: false,
    disabled: false,
    filters: [],
    filterErrors: null,
    defaultFilters: [],
    currentlyOpenFilter: null,
    currentlyOpenDefaultFilter: null,
    refreshControls: null,
    showDefaultFilterMenu: true,
    styles: undefined
};

const mapStateToProps = (state, ownProps) => ({
    filters: selectors.getFilters(state, ownProps.id),
    defaultFilters: selectors.getDefaultFilters(state, ownProps.id),
    actions: actionFactory(ownProps.id),
    currentlyOpenFilter: selectors.getCurrentlyOpenFilter(state, ownProps.id),
    currentlyOpenDefaultFilter: selectors.getCurrentlyOpenDefaultFilter(state, ownProps.id)
});

const mapDispatchToProps = (dispatch) => ({
    loaded: (id) => dispatch(internalActions.loaded(id)),
    dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterHeader);
